/* eslint-disable @typescript-eslint/no-explicit-any */
import { DataGridComponents } from '@neo4j-ndl/react';
import { TrashIconOutline } from '@neo4j-ndl/react/icons';
import { isNotNullish } from '@nx/stdlib';
import { DataGridHelpers } from '@nx/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { useCommonTable } from '../components/table/common-table';
import type { AllowListEntry } from './types';

const columnHelper = createColumnHelper<AllowListEntry>();

type AllowListTableProps = {
  allowList: AllowListEntry[];
  onRemoveEntry: (address: string) => void;
};

export const AllowListTable = ({ allowList, onRemoveEntry }: AllowListTableProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor('IpRange', {
        header: 'Address/Range',
        size: 100,
      }),
      columnHelper.accessor('Description', {
        header: 'Description',
        cell: (cx) => (isNotNullish(cx.getValue()) ? cx.getValue() : ''),
      }),
      columnHelper.display({
        id: 'remove',
        cell: (cx) => (
          <DataGridComponents.RowActionCell
            cell={cx}
            innerIconButtonProps={{
              children: <TrashIconOutline />,
              onClick: () => {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                (cx.table.options.meta as { onRemoveEntry: (IpRange: string) => void }).onRemoveEntry(
                  cx.row.original.IpRange,
                );
              },
              ariaLabel: `Delete CIDR`,
            }}
          />
        ),
        enableResizing: false,
        meta: {
          isActionCell: {
            actions: [],
          },
        },
      }),
    ],
    [],
  );

  const table = useCommonTable({
    columns,
    data: allowList,
    enableColumnPinning: true,
    state: {
      columnPinning: {
        right: ['actions'],
      },
    },
    meta: { onRemoveEntry },
  });

  return (
    <div className="border-neutral-border-weak  overflow-hidden rounded-sm border">
      <DataGridHelpers.DataGridRightColumnPinned<AllowListEntry>
        rootProps={{
          className: 'n-h-full',
        }}
        isLoading={false}
        tableInstance={table}
        components={{
          Navigation: null,
          NoDataPlaceholder: null,
        }}
        styling={{
          hasHoverEffects: true,
          borderStyle: 'all-sides',
        }}
      />
    </div>
  );
};
